import { useMemo } from 'react';
import { useGlobalStore } from '../stores/globalStore';
import type { MealPatternFacilitySettings } from '../types/mealPattern';
import { patternLabels, type PatternLabels } from '../utils/patternLabels';
import { displayEhrTitle } from '../utils/ehrDisplayName';

/**
 * Sensible defaults for when facility settings haven't loaded yet.
 */
const DEFAULTS: MealPatternFacilitySettings = {
  mealPatternShortDescription: 'Meal Pattern',
  mealPatternLongDescription: 'Meal Pattern',
  adjustmentsShortDescription: 'Adjustments',
  adjustmentsLongDescription: 'Adjustments',
  mealPattern_StartDay: 0,
  mealPattern_ShowWarnings: true,
  aim_DietOrder_UpdateMealPattern: false,
  aim_DietOrder_UpdateAdjustments: false,
  aim_DietOrder_UpdateSelectiveMenus: false,
  aim_MealPattern_UpdatePreviousMenus: false,
  aim_Item_UpdateMealPattern: false,
  adjustment_ShowReason: false,
  fKey_Diet_TypeNPO: null,
  fluid_Warning: 0,
  mp_ShowChewingAbility: false,
  feedingStatus_PRI_Visible: true,
  feedingStatus_MDS_Visible: false,
  feedingStatus_Facility_Visible: false,
  showSpecialDeviceMealtimes: false,
  bulkFirstChoice: false,
  residentLabel: 'Resident',
  residentPossessiveLabel: "Resident's",
  setName: 'Set',
  ccMlLabel: 'cc/mL',
  specialDeviceLabel: 'Special Device',
  facilityLabel: 'Facility',
  facilityEhrActive: false,
  ehrTitle: 'EHR',
  ehr_DoNotProcess_Allergies: false,
};

/**
 * Derives MealPatternFacilitySettings from the global facility settings store.
 *
 * The full TblFacility is already fetched and stored in useGlobalStore.facilitySettings.
 * This hook extracts only the properties relevant to meal pattern behavior and
 * provides sensible defaults when the settings haven't loaded yet.
 */
export function useMealPatternFacilitySettings(): {
  settings: MealPatternFacilitySettings;
  isLoaded: boolean;
} {
  const facilitySettings = useGlobalStore((s) => s.facilitySettings);
  const isLoaded = useGlobalStore((s) => s.facilitySettingsLoaded);

  const settings = useMemo<MealPatternFacilitySettings>(() => {
    if (!facilitySettings) return DEFAULTS;

    return {
      mealPatternShortDescription:
        facilitySettings.mealPatternShortDescription || DEFAULTS.mealPatternShortDescription,
      mealPatternLongDescription:
        facilitySettings.mealPatternLongDescription || DEFAULTS.mealPatternLongDescription,
      adjustmentsShortDescription:
        facilitySettings.adjustmentsShortDescription || DEFAULTS.adjustmentsShortDescription,
      adjustmentsLongDescription:
        facilitySettings.adjustmentsLongDescription || DEFAULTS.adjustmentsLongDescription,
      mealPattern_StartDay: facilitySettings.mealPattern_StartDay ?? DEFAULTS.mealPattern_StartDay,
      mealPattern_ShowWarnings: facilitySettings.mealPattern_ShowWarnings ?? DEFAULTS.mealPattern_ShowWarnings,
      aim_DietOrder_UpdateMealPattern: facilitySettings.aIM_DietOrder_UpdateMealPattern ?? DEFAULTS.aim_DietOrder_UpdateMealPattern,
      aim_DietOrder_UpdateAdjustments: facilitySettings.aIM_DietOrder_UpdateAdjustments ?? DEFAULTS.aim_DietOrder_UpdateAdjustments,
      aim_DietOrder_UpdateSelectiveMenus: facilitySettings.aIM_DietOrder_UpdateSelectiveMenus ?? DEFAULTS.aim_DietOrder_UpdateSelectiveMenus,
      aim_MealPattern_UpdatePreviousMenus: facilitySettings.aIM_MealPattern_UpdatePreviousMenus ?? DEFAULTS.aim_MealPattern_UpdatePreviousMenus,
      aim_Item_UpdateMealPattern: facilitySettings.aIM_Item_UpdateMealPattern ?? DEFAULTS.aim_Item_UpdateMealPattern,
      adjustment_ShowReason: facilitySettings.adjustment_ShowReason ?? DEFAULTS.adjustment_ShowReason,
      fKey_Diet_TypeNPO: facilitySettings.fKey_Diet_TypeNPO ?? DEFAULTS.fKey_Diet_TypeNPO,
      fluid_Warning: facilitySettings.fluid_Warning ?? DEFAULTS.fluid_Warning,
      mp_ShowChewingAbility: facilitySettings.mP_ShowChewingAbility ?? DEFAULTS.mp_ShowChewingAbility,
      feedingStatus_PRI_Visible: facilitySettings.feedingStatus_PRI_Visible ?? DEFAULTS.feedingStatus_PRI_Visible,
      feedingStatus_MDS_Visible: facilitySettings.feedingStatus_MDS_Visible ?? DEFAULTS.feedingStatus_MDS_Visible,
      feedingStatus_Facility_Visible: facilitySettings.feedingStatus_Facility_Visible ?? DEFAULTS.feedingStatus_Facility_Visible,
      showSpecialDeviceMealtimes: facilitySettings.showSpecialDeviceMealtime ?? DEFAULTS.showSpecialDeviceMealtimes,
      bulkFirstChoice: facilitySettings.bulkFirstChoice ?? DEFAULTS.bulkFirstChoice,
      residentLabel: facilitySettings.resident || DEFAULTS.residentLabel,
      residentPossessiveLabel: facilitySettings.resident_Possessive || DEFAULTS.residentPossessiveLabel,
      setName: facilitySettings.setName || DEFAULTS.setName,
      ccMlLabel: facilitySettings.cC_ML_Label || DEFAULTS.ccMlLabel,
      specialDeviceLabel: facilitySettings.specialDevice || DEFAULTS.specialDeviceLabel,
      facilityLabel: facilitySettings.facility || DEFAULTS.facilityLabel,
      facilityEhrActive: facilitySettings.facilityEhrActive ?? DEFAULTS.facilityEhrActive,
      ehrTitle: displayEhrTitle(facilitySettings.eHR_Title) || DEFAULTS.ehrTitle,
      ehr_DoNotProcess_Allergies: facilitySettings.eHR_DoNotProcess_Allergies ?? DEFAULTS.ehr_DoNotProcess_Allergies,
    };
  }, [facilitySettings]);

  return { settings, isLoaded };
}

/**
 * The facility's own names for meal patterns and adjustments, ready for
 * titles, buttons and dialogs. Facilities rename both ("Standing Order",
 * "Standing Orders"), so the resident page must never hard-code
 * "Meal Pattern". See utils/patternLabels.ts for the plural handling.
 */
export function usePatternLabels(): { mealPattern: PatternLabels; adjustments: PatternLabels } {
  const { settings } = useMealPatternFacilitySettings();
  const {
    mealPatternShortDescription, mealPatternLongDescription,
    adjustmentsShortDescription, adjustmentsLongDescription,
  } = settings;
  return useMemo(() => ({
    mealPattern: patternLabels(mealPatternShortDescription, mealPatternLongDescription),
    adjustments: patternLabels(adjustmentsShortDescription, adjustmentsLongDescription),
  }), [mealPatternShortDescription, mealPatternLongDescription, adjustmentsShortDescription, adjustmentsLongDescription]);
}
